import { useWidgetContext } from '../context/WidgetContext';
import AmplitudeUtils from '../../utils/amplitude-utils';
import { getPasswordResetUrlByClientId, isDirectOhswAdjacentAuthClient, isAthenaOneWebClient } from '../../utils/app-utils';
import { Button } from '@athena/forge';
import { getLocalizedString } from '../../utils/i18n-utils';
import { launchLwa } from '../../utils/lwa-utils';
import athenaConfig from '../../config';
import { APP_TYPES, CREDENTIAL_TYPES } from '../../constants';
import { clearLastIdentifier, getUsername, showFormErrors } from '../../utils/widget-facade';
import { detectCredentialType } from '../../utils/user-utils';
import { userPasswordResetInit, getTransformedError } from '../../utils/password-reset-api';

const FORGOT_PASSWORD_HASH = '#signin/athena-forgot-password';

interface ForgotPasswordLinkProps {
  text?: string;
}

export default function ForgotPasswordLink({
  text = getLocalizedString('forgotpassword'),
}: ForgotPasswordLinkProps = {}) {
  const {
    settings: { clientId, isAuthnWithLwaClientId, appType },
    athenaOneWebSso,
    athenaOneWebNonSsoTimedOut,
  } = useWidgetContext();
  // Hide forgot password link for sso users and for iframed non sso users
  // The link is hidden for iframed non SSO users to prevent them from facing FIDO2 prompts within iframe
  if (athenaOneWebSso || athenaOneWebNonSsoTimedOut) return <></>;

  // Show forgot password link for AthenaOne web clients, LWA clients, or direct-OHSW adjacent auth apps.
  const shouldLinkBeDisplayed = isAthenaOneWebClient() || isAuthnWithLwaClientId || isDirectOhswAdjacentAuthClient();

  // Check if native password reset is enabled via feature flag
  const useNativeFlow = athenaConfig.featureFlags.enableNativePasswordReset;

  if (shouldLinkBeDisplayed) {
    return (
      <Button
        text={text}
        variant={'tertiary'}
        className="inline-button"
        onClick={async () => {
          await AmplitudeUtils.logCustomEvent('ForgotPasswordButtonClick', {});

          if (useNativeFlow &&
              (isAthenaOneWebClient() || (isAuthnWithLwaClientId && appType === APP_TYPES.ATHENANET_USER))) {
            clearLastIdentifier();
            // Find and trigger click on Okta's native forgot password link
            const $forgotPasswordLink = jQueryCourage('a[data-se="forgot-password"]');

            if ($forgotPasswordLink.length > 0) {
              $forgotPasswordLink.trigger('click');
            }
            else {
              // Fallback: redirect to URL based on client mapping
              const passwordResetUrl = getPasswordResetUrlByClientId(clientId);
              if (passwordResetUrl) {
                window.location.assign(passwordResetUrl);
              }
            }
          }
          else if (isDirectOhswAdjacentAuthClient()) {
            const identifier = getUsername();
            if (appType !== APP_TYPES.ADJACENT_AONE_ATHENISTA_AUTH) {
              await launchLwa({ hash: FORGOT_PASSWORD_HASH, identifier });
              return;
            }

            const credentialType = detectCredentialType(identifier);
            if (
              credentialType === CREDENTIAL_TYPES.EMAIL ||
              credentialType === CREDENTIAL_TYPES.UNKNOWN
            ) {
              await launchLwa({ hash: FORGOT_PASSWORD_HASH, identifier });
            }
            else if (
              credentialType === CREDENTIAL_TYPES.ANET_USERNAME ||
              credentialType === CREDENTIAL_TYPES.NAMESPACED_ANET_USERNAME
            ) {
              clearLastIdentifier();
              try {
                await userPasswordResetInit({ username: identifier });
              }
              catch (error: any) {
                // Do not advance into the reset flow when the eligibility/group-assignment
                // call fails (e.g. Lambda timeout or 5xx) - the user would hit a confusing
                // downstream error instead of an actionable message.
                AmplitudeUtils.logCustomEvent('PasswordReset::InitFailed', {
                  username: identifier,
                  errorMessage: error instanceof Error ? error.message : 'Unknown error',
                  errorCode: error?.errorCode,
                });
                showFormErrors(getTransformedError(error));
                return;
              }
              const $forgotPasswordLink = jQueryCourage('a[data-se="forgot-password"]');
              $forgotPasswordLink.trigger('click');
            }
          }
          else if (isAthenaOneWebClient()) {
            // Flag-disabled path for athenaOne web clients. This previously pointed at
            // athenaNet's resetpassword.esp, which has been retired (UIAMUA-8419), so
            // route to the OHSW-hosted reset page instead.
            const passwordResetUrl = getPasswordResetUrlByClientId(clientId);
            if (passwordResetUrl) {
              window.location.assign(passwordResetUrl);
            }
            else {
              await AmplitudeUtils.logCustomEvent('ForgotPassword::ResetPasswordLinkUnavailable', { clientId });
            }
          }
          else {
            // Use existing LWA flow for non-athenaOne clients
            await launchLwa({ hash: FORGOT_PASSWORD_HASH });
          }
        }}
      />
    );
  }

  return <></>;
}
